import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Users2,
  BookOpen,
  GraduationCap,
  DollarSign,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  LayoutDashboard,
  MessageSquare,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export function SchoolSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const text = {
    fr: {
      dashboard: 'Tableau de bord',
      students: 'Élèves',
      classes: 'Classes',
      teachers: 'Enseignants',
      schoolFees: 'Frais Scolaires',
      payments: 'Paiements',
      communication: 'Communiquer',
      settings: 'Paramètres',
      logout: 'Déconnexion',
    },
    en: {
      dashboard: 'Dashboard',
      students: 'Students',
      classes: 'Classes',
      teachers: 'Teachers',
      schoolFees: 'School Fees',
      payments: 'Payments',
      communication: 'Communication',
      settings: 'Settings',
      logout: 'Logout',
    }
  };

  const currentText = text[language as keyof typeof text];

  const menuItems = [
    { 
      text: currentText.dashboard, 
      icon: LayoutDashboard, 
      path: `/${language}/school-dashboard`,
    },
    { 
      text: currentText.students, 
      icon: Users2, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'eleves' : 'students'}`,
    },
    { 
      text: currentText.classes, 
      icon: BookOpen, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'classes' : 'classes'}`,
    },
    { 
      text: currentText.teachers, 
      icon: GraduationCap, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'enseignants' : 'teachers'}`,
    },
    { 
      text: currentText.schoolFees, 
      icon: DollarSign, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'frais-scolaires' : 'school-fees'}`,
    },
    { 
      text: currentText.payments, 
      icon: CreditCard, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'paiements' : 'payments'}`,
    },
    { 
      text: currentText.communication, 
      icon: MessageSquare, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'communiquer' : 'communication'}`,
    },
    { 
      text: currentText.settings, 
      icon: Settings, 
      path: `/${language}/school-dashboard/${language === 'fr' ? 'parametres' : 'settings'}`,
    },
  ];

  const handleLogout = () => {
    logout();
    navigate(`/${language}/login`);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, backgroundColor: '#0D3C4C' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#1A5F7A', width: 48, height: 48 }}>
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: 'white', 
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.username}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {user?.company?.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      <List sx={{ flex: 1, py: 2, overflowY: 'auto' }}>
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: '8px',
                  backgroundColor: isActive ? 'rgba(13, 60, 76, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'rgba(13, 60, 76, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon 
                    size={20} 
                    color={isActive ? '#0D3C4C' : '#666'}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#0D3C4C' : '#666',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      <List sx={{ py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogOut size={20} color="#d32f2f" />
            </ListItemIcon>
            <ListItemText 
              primary={currentText.logout}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                color: '#d32f2f',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ 
          display: { sm: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          backgroundColor: 'white',
          boxShadow: 2,
          '&:hover': {
            backgroundColor: 'white',
          }
        }}
      >
        <Menu />
      </IconButton>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
